import { useState } from "react";
import { Minus, Plus, ArrowRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useSite } from "@/context/SiteContext";
import { UI } from "@/i18n";
import { scrollToId } from "@/App";
import Reveal, { fadeUp, stagger } from "./Reveal";

export default function QuoteCalculator() {
  const { content, t } = useSite();
  const calc = content?.calculator;
  const [typeId, setTypeId] = useState(null);
  const [qty, setQty] = useState(1);
  const [premium, setPremium] = useState(false);

  if (!calc?.types?.length) return null;
  const type = calc.types.find((x) => x.id === typeId) || calc.types[0];
  const factor = premium ? 1.35 : 1;
  const round10 = (n) => Math.round(n / 10) * 10;
  const lo = round10(type.min * qty * factor);
  const hi = round10(type.max * qty * factor);

  const request = () => {
    const msg = `Quote request: ${qty}x ${t(type.label)} (${premium ? t(UI.calc.premium) : t(UI.calc.standard)}) - est. $${lo}-$${hi}`;
    window.dispatchEvent(new CustomEvent("ct-prefill-quote", { detail: msg }));
    scrollToId("contact");
  };

  return (
    <section id="calculator" className="py-24 sm:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="stitch-x w-10 text-brand-orange" />
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-brand-orange">{t(UI.calc.eyebrow)}</span>
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[1.02]">{t(UI.calc.title)}</h2>
        </Reveal>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid lg:grid-cols-2 gap-6"
        >
          <motion.div variants={fadeUp} className="rounded-xl border border-white/10 bg-brand-surface p-8 space-y-7">
            <div>
              <p className="font-mono2 text-xs uppercase tracking-widest text-white/50 mb-3">{t(UI.calc.pieces)}</p>
              <div className="flex flex-wrap gap-2" data-testid="calc-types">
                {calc.types.map((tp) => (
                  <button
                    key={tp.id}
                    onClick={() => setTypeId(tp.id)}
                    data-testid={`calc-type-${tp.id}`}
                    className={`px-4 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                      type.id === tp.id
                        ? "bg-brand-blue border-brand-blue text-white"
                        : "border-white/20 text-white/70 hover:border-brand-blue hover:text-white"
                    }`}
                  >
                    {t(tp.label)}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-7">
              <div>
                <p className="font-mono2 text-xs uppercase tracking-widest text-white/50 mb-3">{t(UI.calc.quantity)}</p>
                <div className="inline-flex items-center gap-4 border border-white/15 rounded-full px-4 py-2">
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    data-testid="calc-qty-minus"
                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                    aria-label="Decrease"
                  >
                    <Minus size={15} />
                  </button>
                  <span className="font-display text-2xl w-8 text-center" data-testid="calc-qty-value">{qty}</span>
                  <button
                    onClick={() => setQty(Math.min(50, qty + 1))}
                    data-testid="calc-qty-plus"
                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                    aria-label="Increase"
                  >
                    <Plus size={15} />
                  </button>
                </div>
              </div>
              <div>
                <p className="font-mono2 text-xs uppercase tracking-widest text-white/50 mb-3">{t(UI.calc.quality)}</p>
                <button
                  onClick={() => setPremium(!premium)}
                  data-testid="calc-quality-toggle"
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-semibold transition-all ${
                    premium ? "bg-brand-orange border-brand-orange text-brand-navy" : "border-white/20 text-white/70 hover:border-brand-orange"
                  }`}
                >
                  {premium && <Check size={14} strokeWidth={3} />}
                  {premium ? t(UI.calc.premium) : t(UI.calc.standard)}
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="relative rounded-xl border-2 border-dashed border-brand-orange/50 bg-[linear-gradient(160deg,rgba(46,155,232,0.12),rgba(249,115,22,0.08))] p-8 flex flex-col justify-center items-start overflow-hidden"
          >
            <p className="font-mono2 text-xs uppercase tracking-widest text-white/60">{t(UI.calc.estimate)}</p>
            <p className="font-display text-5xl sm:text-6xl mt-3 text-transparent bg-clip-text bg-[linear-gradient(90deg,#2E9BE8,#F97316)]" data-testid="calc-estimate-value">
              ${lo.toLocaleString()} - ${hi.toLocaleString()}
            </p>
            <p className="text-xs text-white/50 mt-3">{t(UI.calc.note)}</p>
            <button
              onClick={request}
              data-testid="calc-request-btn"
              className="mt-6 inline-flex items-center gap-2 bg-brand-red hover:bg-red-600 text-white font-bold px-6 py-3.5 rounded-full transition-all hover:scale-[1.03]"
            >
              {t(UI.calc.request)} <ArrowRight size={16} />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
